import type { MessageDescriptor } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { fragmentContainsNodeType } from '@atlaskit/editor-common/insert';
import { isNestedTablesSupported } from '@atlaskit/editor-common/nesting';
import { Slice } from '@atlaskit/editor-prosemirror/model';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';

import type { InsertedContentNodeTransformer } from '../../../../editor-actions/types';
import { eventHub } from '../../../../pm-plugins/ai-event-hub/event-hub';
import { findEditorIdFromView } from '../../../../pm-plugins/ai-event-hub/utils/isolated-utils';
import { InsertAtOrBelowIcon } from '../../../icons/actions/insertAtOrBelow';
import { InsertAtTopIcon } from '../../../icons/actions/insertAtTop';
import type {
	ActionAppearance,
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemMarkdownAction,
} from '../config-items';
import {
	beforeDispatchAIRovoAgentTransaction,
	beforeDispatchAITransaction,
	beforeDispatchingTransactionForBlocks,
	insertAIContentAtCurrentPosition,
	insertContentAfterTable,
	replaceWithAIContent,
	replaceWithAIContentForBlock,
	shouldInsertAfterTable,
} from '../utils/action-utils';

export type CreateInsertProps = {
	appearance: ActionAppearance;
	factoryOptions?: EditorPluginAIConfigItemFactoryOptions;
	insertedContentNodeTransformer?: InsertedContentNodeTransformer;
	isRovoAgentAction?: boolean;
	title?: MessageDescriptor;
};

export const createInsertAtTop = ({
	appearance,
	insertedContentNodeTransformer,
}: CreateInsertProps): EditorPluginAIConfigItemMarkdownAction => ({
	type: 'markdown',
	title: aiConfigItemMessages.configActionInsertAtTop,
	appearance,
	run({ editorView, pmFragment }) {
		const { state } = editorView;
		const transaction = state.tr;

		const slice = new Slice(pmFragment, 0, 0);
		transaction.replace(0, 0, slice);

		beforeDispatchAITransaction(transaction, state, insertedContentNodeTransformer);
		editorView.dispatch(transaction);
	},
	icon: InsertAtTopIcon,
});

export const createInsertBelow = ({
	appearance,
	insertedContentNodeTransformer,
	factoryOptions,
}: CreateInsertProps): EditorPluginAIConfigItemMarkdownAction => ({
	type: 'markdown',
	title: factoryOptions?.isInDocumentStreamingEnabled
		? aiConfigItemMessages.configActionInsertBelowOriginal
		: aiConfigItemMessages.configActionInsertBelow,
	appearance,
	run({ editorView, positions, pmFragment, triggeredFor }) {
		const { state } = editorView;
		const transaction = state.tr;

		if (
			fragmentContainsNodeType(pmFragment, state.schema.nodes.table) &&
			shouldInsertAfterTable({
				selection: state.selection,
				tableSchema: state.schema.nodes.table,
			}) &&
			isNestedTablesSupported(state.schema)
		) {
			insertContentAfterTable({ pmFragment, transaction });
		} else {
			if (triggeredFor?.isBlock) {
				const insertPos = positions[1];
				safeInsert(pmFragment, insertPos)(transaction);
				beforeDispatchingTransactionForBlocks(pmFragment, transaction, editorView.state);
			} else {
				let insertPos: number;
				/* eslint-disable no-unused-vars */
				try {
					insertPos = state.doc.resolve(positions[1]).after();
				} catch (error) {
					insertPos = positions[1];
				}
				/* eslint-enable no-unused-vars */
				safeInsert(pmFragment, insertPos)(transaction);
			}
		}

		beforeDispatchAITransaction(transaction, state, insertedContentNodeTransformer);
		editorView.dispatch(transaction);
	},
	icon: InsertAtOrBelowIcon,
});

export const createInsertAtCurrentPosition = ({
	appearance,
	insertedContentNodeTransformer,
	isRovoAgentAction,
	title = aiConfigItemMessages.configActionInsert,
}: CreateInsertProps): EditorPluginAIConfigItemMarkdownAction => ({
	type: 'markdown',
	title,
	appearance,
	isRovoAgentAction,
	run({ editorView, positions, pmFragment }) {
		const tr = insertAIContentAtCurrentPosition({
			editorView,
			positions,
			pmFragment,
			insertedContentNodeTransformer,
		});
		if (isRovoAgentAction) {
			beforeDispatchAIRovoAgentTransaction(tr);
		}

		editorView.dispatch(tr);
	},
	icon: InsertAtOrBelowIcon,
});

export const createReplace = ({
	appearance,
	insertedContentNodeTransformer,
	isRovoAgentAction,
}: CreateInsertProps): EditorPluginAIConfigItemMarkdownAction => ({
	type: 'markdown',
	title: aiConfigItemMessages.configActionReplace,
	appearance,
	isRovoAgentAction,
	run({ editorView, positions, pmFragment, selectionMetaData, triggeredFor }) {
		const tr = triggeredFor?.isBlock
			? replaceWithAIContentForBlock({
					editorView,
					positions,
					pmFragment,
					insertedContentNodeTransformer,
				})
			: replaceWithAIContent({
					editorView,
					positions,
					pmFragment,
					selectionMetaData,
					insertedContentNodeTransformer,
				});

		if (isRovoAgentAction) {
			beforeDispatchAIRovoAgentTransaction(tr);
		}

		editorView.dispatch(tr);
	},
});

/**
 * EXPERIMENTAL: Util for experimental flow where 'Suggest title' in
 * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
 * This util allows replacing the title in Confluence.
 */
export const createReplaceExistingConfluenceTitle = ({
	appearance,
}: CreateInsertProps): EditorPluginAIConfigItemMarkdownAction => ({
	type: 'markdown',
	title: aiConfigItemMessages.configActionReplaceTitle,
	appearance,
	getDisabledState: ({ markdown, formatMessage }) => {
		const responseLength = markdown?.length ?? 0;
		// Source: next/packages/editor-title/src/EditorTitle.tsx
		const MAX_TITLE_LENGTH = 255;
		if (responseLength >= MAX_TITLE_LENGTH) {
			return {
				isDisabled: true,
				tooltip: formatMessage(
					aiConfigItemMessages.configActionReplaceTitleTooLongDisabledTooltip,
					{ maxLength: MAX_TITLE_LENGTH },
				),
			};
		}
		return { isDisabled: false };
	},
	run({ editorView, pmFragment }) {
		const fragmentToInsert = pmFragment;
		let textContent = '';
		fragmentToInsert.forEach((node) => {
			textContent += node.textContent;
		});
		const confluenceTitle = document.querySelector(
			'textarea[data-ai-suggest-title-target="true"]',
		) as HTMLTextAreaElement;
		if (confluenceTitle) {
			const sourceEditorId = findEditorIdFromView(editorView);
			if (sourceEditorId) {
				eventHub.publishFromEditor({
					event: 'replace title in confluence',
					data: { analyticSourceId: 'confluenceTitleToolbar', sourceEditorId, title: textContent },
				});
			}

			confluenceTitle.scrollIntoView();
		}
	},
});
